import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  NumberInput,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconBolt, IconCheck } from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { SectionHeading } from '@/components/section-heading';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  getLlmStatus,
  listLlmModels,
  listLlmSettings,
  pingLlm,
  updateLlmSettings,
  TIER_BLURB,
  type LlmModel,
  type LlmOverrides,
  type LlmPingResult,
  type LlmTierSettings,
} from '@/lib/llm';
import classes from './settings-page.module.css';

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/** A NumberInput hands back '' for an empty box; the API wants null. */
function toNullableNumber(value: string | number): number | null {
  if (value === '' || value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

interface TierCardProps {
  settings: LlmTierSettings;
  models: LlmModel[];
  /** True when the model list could not be fetched, rather than being empty. */
  modelsUnavailable: boolean;
  onSaved: (saved: LlmTierSettings) => void;
}

/**
 * One tier's settings.
 *
 * The card holds the whole tier rather than patching field by field, so Save
 * sends all five values and an emptied box arrives as an explicit null — which
 * is what clears an override and returns that setting to its default.
 */
function TierCard({
  settings,
  models,
  modelsUnavailable,
  onSaved,
}: TierCardProps) {
  const [draft, setDraft] = useState<LlmOverrides>(settings.overrides);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [ping, setPing] = useState<LlmPingResult | null>(null);
  const [pingError, setPingError] = useState<unknown>(null);
  const [isPinging, setIsPinging] = useState(false);

  const { defaults, effective } = settings;

  async function save(event: FormEvent) {
    event.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    try {
      const saved = await updateLlmSettings(settings.tier, {
        ...draft,
        model: draft.model?.trim() ? draft.model.trim() : null,
      });
      onSaved(saved);
      setDraft(saved.overrides);
      notifications.show({
        color: 'brand',
        message: `${settings.tier} now runs on ${saved.effective.model}.`,
      });
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  async function test() {
    setIsPinging(true);
    setPingError(null);
    setPing(null);
    try {
      setPing(await pingLlm(settings.tier));
    } catch (caught) {
      setPingError(caught);
    } finally {
      setIsPinging(false);
    }
  }

  return (
    <Card>
      <form onSubmit={(event) => void save(event)} noValidate>
        <Stack gap="md">
          <SectionHeading
            title={settings.tier}
            subtitle={TIER_BLURB[settings.tier]}
            actions={
              <Button
                variant="light"
                size="xs"
                leftSection={<IconBolt size={14} />}
                loading={isPinging}
                onClick={() => void test()}
              >
                Test
              </Button>
            }
          />

          {saveError !== null && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              <Text size="sm">{describeError(saveError)}</Text>
            </Alert>
          )}

          <Autocomplete
            label="Model"
            description={
              models.length > 0
                ? 'Anything this key can reach. Leave blank to use the default.'
                : modelsUnavailable
                  ? 'The model list could not be loaded, so type an id. Leave blank to use the default.'
                  : 'Type a model id. Leave blank to use the default.'
            }
            placeholder={defaults.model}
            data={models.map((model) => model.id)}
            value={draft.model ?? ''}
            onChange={(value) =>
              setDraft({ ...draft, model: value === '' ? null : value })
            }
          />

          <Group grow align="flex-start">
            <NumberInput
              label="Timeout"
              description="Milliseconds before a call is abandoned."
              placeholder={String(defaults.timeoutMs)}
              min={500}
              max={120000}
              allowDecimal={false}
              value={draft.timeoutMs ?? ''}
              onChange={(value) =>
                setDraft({ ...draft, timeoutMs: toNullableNumber(value) })
              }
            />
            <NumberInput
              label="Attempts"
              description="Including the first. Every retry bills again."
              placeholder={String(defaults.maxAttempts)}
              min={1}
              max={5}
              allowDecimal={false}
              value={draft.maxAttempts ?? ''}
              onChange={(value) =>
                setDraft({ ...draft, maxAttempts: toNullableNumber(value) })
              }
            />
          </Group>

          <Group grow align="flex-start">
            <NumberInput
              label="Output token cap"
              description="A ceiling nothing can exceed. The one setting that bounds a bill — set it too low and generations are cut off rather than failing."
              placeholder="No cap"
              min={1}
              max={65536}
              allowDecimal={false}
              value={draft.maxOutputTokens ?? ''}
              onChange={(value) =>
                setDraft({ ...draft, maxOutputTokens: toNullableNumber(value) })
              }
            />
            <NumberInput
              label="Thinking budget"
              description="Reasoning tokens before answering. 0 turns it off — the biggest latency saving here, and the easiest way to quietly make output worse."
              placeholder="Model decides"
              min={-1}
              max={32768}
              allowDecimal={false}
              value={draft.thinkingBudget ?? ''}
              onChange={(value) =>
                setDraft({ ...draft, thinkingBudget: toNullableNumber(value) })
              }
            />
          </Group>

          {pingError !== null && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              <Text size="sm">{describeError(pingError)}</Text>
            </Alert>
          )}

          {ping !== null && (
            <Alert color="teal" icon={<IconCheck size={18} />}>
              <Stack gap={4}>
                <Text size="sm">{ping.text}</Text>
                <Text size="xs" c="dimmed">
                  {ping.call.model} · {ping.call.latencyMs}ms · in{' '}
                  {ping.call.promptTokens ?? '?'} · out{' '}
                  {ping.call.outputTokens ?? '?'} · thought{' '}
                  {ping.call.thoughtTokens ?? '?'} · {ping.call.finishReason}
                </Text>
              </Stack>
            </Alert>
          )}

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              Now running <Code>{effective.model}</Code>
            </Text>
            <Button type="submit" loading={isSaving}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

export function LlmPage() {
  const [tiers, setTiers] = useState<LlmTierSettings[]>([]);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [modelsUnavailable, setModelsUnavailable] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [status, settings] = await Promise.all([
          getLlmStatus(),
          listLlmSettings(),
        ]);
        if (cancelled) return;
        setConfigured(status.configured);
        setTiers(settings);
        setLoadError(null);

        // Separately, and allowed to fail. Without a key there is no list to
        // fetch, and the screen still has to work — the model field falls back
        // to free text rather than the page falling over.
        if (status.configured) {
          try {
            const list = await listLlmModels();
            if (!cancelled) setModels(list);
          } catch (caught) {
            if (cancelled) return;
            setModels([]);
            // An empty dropdown because there is no key is expected and already
            // said elsewhere on the page. An empty dropdown because the call
            // failed is not, and an operator staring at a blank list deserves
            // to know which of the two happened.
            setModelsUnavailable(
              !(
                caught instanceof ApiError &&
                caught.is(ApiErrorCode.LLM_NOT_CONFIGURED)
              ),
            );
          }
        }
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  function onSaved(saved: LlmTierSettings) {
    setTiers((current) =>
      current.map((item) => (item.tier === saved.tier ? saved : item)),
    );
  }

  const header = (
    <PageHeader
      title="Language model"
      description="What each tier runs on, and what one call is allowed to cost. Changes apply to the next call — nothing needs restarting."
      actions={
        <Badge
          variant="light"
          color={configured ? 'teal' : 'gray'}
          size="lg"
        >
          {configured ? 'Key configured' : 'No key'}
        </Badge>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the model settings"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {!configured && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">
            No API key is set on this server, so nothing here can reach a model
            yet. The key lives in the API&rsquo;s environment and is deliberately
            not editable here &mdash; it is a credential, not a setting. These
            values can still be set and will apply once a key is in place.
          </Text>
        </Alert>
      )}

      {tiers.map((settings) => (
        <TierCard
          key={settings.tier}
          settings={settings}
          models={models}
          modelsUnavailable={modelsUnavailable}
          onSaved={onSaved}
        />
      ))}

      <Text size="xs" c="dimmed">
        A blank field means the tier uses the value the API was deployed with,
        shown as the placeholder. Clearing a box is how a setting is put back.
        Test spends real tokens on every press &mdash; a few, not many, but it is
        a real call to a paid model rather than a health check.
      </Text>
    </Stack>
  );
}
